import React from "react";
import {
  CreditCardIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

const SubscriptionOverviewTab = ({
  organization,
  isOrgOwner,
  availablePlans,
  getPlanDisplayName,
  formatUsage,
  currencySymbol,
  setActiveSubTab,
  SUB_TABS,
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">
          Subscription Overview
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Your current plan and subscription status at a glance
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <CreditCardIcon className="h-7 w-7 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Plan
                </p>
                <h3 className="text-2xl font-bold text-gray-900 mt-0.5">
                  {getPlanDisplayName(organization?.planId)}
                </h3>
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-2 self-start sm:self-center px-4 py-2 rounded-lg text-sm font-semibold ${
                organization?.isSubscribed
                  ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/20"
                  : "bg-amber-50 text-amber-700 ring-1 ring-amber-500/20"
              }`}
            >
              <CheckCircleIcon className="h-5 w-5" />
              {organization?.isSubscribed ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50/80 border border-gray-100">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <CurrencyDollarIcon className="h-6 w-6 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount per member
                </p>
                <p className="text-base font-bold text-gray-900 truncate mt-0.5">
                  {organization?.planMeta?.[organization?.subscriptionType]
                    ? `${currencySymbol}${
                        organization.subscriptionType?.toLowerCase() === "monthly"
                          ? organization.planMeta[organization.subscriptionType]
                          : organization.planMeta[
                              organization.subscriptionType
                            ] * 12
                      }/${organization.subscriptionType?.toLowerCase() === "monthly" ? "month" : "year"}`
                    : "—"}
                </p>
              </div>
            </div>

            {organization?.planId !== "FREE" && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50/80 border border-gray-100">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <CalendarDaysIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Billing cycle
                  </p>
                  <p className="text-base font-bold text-gray-900 capitalize mt-0.5">
                    {organization?.subscriptionType
                      ? organization.subscriptionType.toLowerCase()
                      : "—"}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50/80 border border-gray-100">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <DocumentTextIcon className="h-6 w-6 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sign requests
                </p>
                <p className="text-base font-bold text-gray-900 mt-0.5">
                  {formatUsage(
                    organization?.usedAllowedSignRequests || 0,
                    organization?.allowedSignRequests || 0
                  )}
                </p>
              </div>
            </div>
          </div>

          {isOrgOwner &&
            (organization?.planId === "FREE" ||
              organization?.planId === "FREE_IN" ||
              availablePlans?.length > 0) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setActiveSubTab(SUB_TABS.upgrade)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary/10 text-primary rounded-lg text-sm font-semibold hover:bg-primary/20 transition-colors"
                >
                  <ArrowTrendingUpIcon className="h-5 w-5" />
                  View upgrade options
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionOverviewTab;
